/**
 * Top bar component with filters and stats
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import FileManagementModal from '../dialogs/FileManagementModal';
import Logo from './Logo';
import { YEAR_CONFIG } from '../../config/yearConfig';
import '../../styles/components/common/TopBar.css';

/**
 * Build year options from configuration boundaries.
 * @param {number} baseYear
 * @param {number} lastDataYear
 * @returns {string[]}
 */
export const buildYearOptionsFromConfig = (baseYear, lastDataYear) => {
  const start = Number(baseYear);
  const end = Number(lastDataYear);

  if (!Number.isInteger(start) || !Number.isInteger(end) || start > end) {
    return [];
  }

  const years = [];
  for (let year = start; year <= end; year++) {
    years.push(year.toString());
  }
  return years;
};

/**
 * Top bar component containing stats, title, and filters
 * @returns {JSX.Element} TopBar component
 */
const TopBar = () => {
  // Local state for file management modal and user menu
  const [showFileManagement, setShowFileManagement] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);

  // Auth context
  const { user, logout } = useAuth();
  
  // Check if user has admin role
  const isAdmin = user?.role === 'admin';
  // File Management is admin-only AND excludes ConferenceManager,
  // except for usernames in CM_MANAGE_OVERRIDES (one-off grants).
  const CM_MANAGE_OVERRIDES = ['mvujadinovic', 'mvujadinovic@abtscs.com'];
  const canManage = isAdmin && (
    user?.code !== 'ConferenceManager' ||
    CM_MANAGE_OVERRIDES.includes(user?.username)
  );
  const currentUsername = user?.username;

  const {
    // Data
    totals,
    managerList,
    clientData,
    filteredClientData,
    pinnedClients,
    orderedPinnedClients,
    zoom,
    fileMetadata,
    
    // Filters
    filters: {
      selectedManager,
      minTotal,
      minLargest,
      searchQuery,
      selectedYear
    },
    activeTierFilter,
    
    // Conference data
    conferences,
    selectedConference,
    setSelectedConference,
    
    // Dynamic slider max values
    sliderMaxValues,
    
    // Filter setters
    setSelectedManager,
    setMinTotal,
    setMinLargest,
    setSearchQuery,
    setSelectedYear,

    // Reset filters
    resetFilters,
    unpinAllClients,
    
    // Pinned clients methods
    pinManagerClients,
    pinTierClients,
    
    // Loading state
    isLoadingClients,
    setIsLoadingClients,
    
    // Pinned list state and setters
    pinnedSearchQuery,
    setPinnedSearchQuery,
    activeTierFilters,
    setActiveTierFilters,
    sortConfig,
    setSortConfig,
    
    // View state
    pinnedViewType,
    useUniformColor,
    isHardPinned,
    
    // View toggles
    toggleViewType,
    toggleUniformColor,
    toggleHardPin,
    
    // App-level state
    useTierColors,
    setUseTierColors,
    
    // Country sidebar state
    selectedCountry,
    setSelectedCountry,
    
    // Client locking functions (if available)
    isClientLocked,
    toggleClientLock,
    setLockedClients
  } = useAppContext();

  // Handle manager selection with auto-pinning
  const handleManagerChange = (e) => {
    const newManager = e.target.value;
    setSelectedManager(newManager);
    
    // Auto-populate pinned clients when a manager is selected
    if (newManager !== 'All' && newManager !== '') {
      // Show loading state
      setIsLoadingClients(true);
      
      // Use filtered data when in History Mode, otherwise use all client data
      const dataToUse = selectedYear !== 'All' ? filteredClientData : clientData;
      pinManagerClients(newManager, dataToUse);
      
      // Hide loading state after 2 seconds
      setTimeout(() => {
        setIsLoadingClients(false);
      }, 2000);
    } else if (newManager === 'All') {
      // Show loading state
      setIsLoadingClients(true);
      
      // When "All" is selected, pin all clients from all managers
      const dataToUse = selectedYear !== 'All' ? filteredClientData : clientData;
      // Clear existing pinned clients first, then pin all clients
      unpinAllClients();
      
      // Use pinTierClients to pin all clients at once (it accepts all clients, not just tier-filtered)
      setTimeout(() => {
        pinTierClients(dataToUse);
        
        // Hide loading state after additional delay
        setTimeout(() => {
          setIsLoadingClients(false);
        }, 1500);
      }, 100); // Small delay to ensure unpinAllClients completes first
    }
  };

  // Handle pinning all tier-filtered clients
  const handlePinTierClients = () => {
    if (!activeTierFilter || !filteredClientData.length) return;
    
    // Use batch pinning to pin all tier clients at once
    pinTierClients(filteredClientData);
  };

  // Handle user menu toggle
  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  // Handle year selection for History Mode
  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
  };

  // Handle conference selection
  const handleConferenceChange = (e) => {
    const newConference = e.target.value;
    setSelectedConference(newConference);
  };

  // Check if Years dropdown should be disabled
  // Rule: Disabled if pinned client list is empty
  const isYearsDropdownDisabled = !pinnedClients || pinnedClients.length === 0;

  // Check if Manager dropdown should be disabled
  // Rule: Disabled when a specific year is selected (not "All Years")
  const isManagerDropdownDisabled = selectedYear !== 'All';

  // Generate year options from YEAR_CONFIG boundaries
  const yearOptions = buildYearOptionsFromConfig(
    YEAR_CONFIG.baseYear,
    YEAR_CONFIG.lastDataYear
  );

  // Handle click outside to close user menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Calculate stats for pinned clients
  const pinnedClientStats = useMemo(() => {
    const activePinnedClients = orderedPinnedClients?.length > 0 ? orderedPinnedClients : pinnedClients;
    
    if (!activePinnedClients || activePinnedClients.length === 0) {
      return null;
    }

    // Calculate unique countries
    const uniqueCountries = new Set(activePinnedClients.map(client => client.country));
    
    // Calculate unique managers
    const uniqueManagers = new Set(activePinnedClients.map(client => client.conference_manager));
    
    // Calculate total groups (entries)
    const totalGroups = activePinnedClients.length;
    
    return {
      entries: totalGroups,
      countries: uniqueCountries.size,
      managers: uniqueManagers.size
    };
  }, [pinnedClients, orderedPinnedClients]);

  // Calculate stats for filtered data (History Mode)
  const filteredDataStats = useMemo(() => {
    if (selectedYear === 'All' || !filteredClientData || filteredClientData.length === 0) {
      return null;
    }

    // Calculate unique countries
    const uniqueCountries = new Set(filteredClientData.map(client => client.country));
    
    // Calculate unique managers
    const uniqueManagers = new Set(filteredClientData.map(client => client.conference_manager));
    
    // Calculate total groups (entries)
    const totalGroups = filteredClientData.length;
    
    return {
      entries: totalGroups,
      countries: uniqueCountries.size,
      managers: uniqueManagers.size
    };
  }, [filteredClientData, selectedYear]);

  // Determine which stats to show - priority: pinned > filtered > totals
  const displayStats = pinnedClientStats || filteredDataStats || totals;
  const isShowingPinnedStats = !!pinnedClientStats;
  const isShowingFilteredStats = !pinnedClientStats && !!filteredDataStats;

  // Format timestamp for display in stats
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'N/A';
    }
  };

  return (
    <>
      <div className="top-bar">
          <div className="top-bar__stats">
            <div className={`top-bar__stats-item ${isShowingPinnedStats ? 'top-bar__stats-item--pinned' : ''}`}>
              <span className="top-bar__stats-value">{displayStats.entries}</span>
              <small className="top-bar__stats-label">Clients</small>
            </div>
            <div className={`top-bar__stats-item ${isShowingPinnedStats ? 'top-bar__stats-item--pinned' : ''}`}>
              <span className="top-bar__stats-value">{displayStats.countries}</span>
              <small className="top-bar__stats-label">Countries</small>
            </div>
            <div className={`top-bar__stats-item ${isShowingPinnedStats ? 'top-bar__stats-item--pinned' : ''}`}>
              <span className="top-bar__stats-value">{displayStats.managers}</span>
              <small className="top-bar__stats-label">Managers</small>
            </div>
            <div className="top-bar__stats-item">
              {/* --- UPDATE: Display the live zoom level from context --- */}
              <span className="top-bar__stats-value">{zoom}</span>
              <small className="top-bar__stats-label">Zoom</small>
            </div>
            {fileMetadata && (
              <>
                <div className="top-bar__stats-item top-bar__stats-item--app-updated">
                  <span className="top-bar__stats-value">{formatTimestamp(fileMetadata.modifiedAt)}</span>
                  <small className="top-bar__stats-label">App Updated</small>
                </div>
              </>
            )}
            {selectedYear !== 'All' && (
              <div className="top-bar__stats-item">
                <span className="top-bar__stats-value">{selectedYear}</span>
                <small className="top-bar__stats-label">History</small>
              </div>
            )}
            {!fileMetadata && (
              <div className="top-bar__stats-item top-bar__stats-item--loading">
                <span className="top-bar__stats-value">...</span>
                <small className="top-bar__stats-label">Loading</small>
              </div>
            )}
          </div>
          
          <div className="top-bar__title-container">
            <Logo size="medium" className="top-bar__logo" />
          </div>
          
          <div className="top-bar__filters">
            <div className="top-bar__filter-item">
              <select 
                className={`top-bar__dropdown ${isYearsDropdownDisabled ? 'disabled' : ''}`}
                value={selectedYear}
                onChange={handleYearChange}
                disabled={isYearsDropdownDisabled}
                title={
                  (!pinnedClients || pinnedClients.length === 0)
                    ? 'Years dropdown is only available when pinned client list is populated'
                    : ''
                }
              >
                <option value="All">All Years</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            
            <div className="top-bar__filter-item">
              <select 
                className={`top-bar__dropdown ${isYearsDropdownDisabled ? 'disabled' : ''}`}
                value={selectedConference}
                onChange={handleConferenceChange}
                disabled={isYearsDropdownDisabled}
                title={
                  (!pinnedClients || pinnedClients.length === 0)
                    ? 'Conference dropdown is only available when pinned client list is populated'
                    : ''
                }
              >
                <option value="All">All Conferences</option>
                {conferences && conferences.map(conference => (
                  <option key={conference} value={conference}>{conference}</option>
                ))}
              </select>
            </div>
            
            <div className="top-bar__filter-item">
              <select className="top-bar__dropdown" disabled>
                <option value="">Select Assignment Preset...</option>
                <option value="profit">Optimized for Profit</option>
                <option value="booking">Optimized for Fast Booking Pace</option>
              </select>
            </div>
            
            <div className="top-bar__filter-item top-bar__filter-item--search">
              <div className="top-bar__search-container">
                <input 
                  type="text" 
                  placeholder="Search by Tier (disabled)" 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="top-bar__search-input"
                  disabled
                />
                {activeTierFilter && (
                  <button 
                    className="top-bar__pin-btn"
                    onClick={handlePinTierClients}
                    title={`Pin all Tier ${activeTierFilter} clients`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 9v8a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V9"></path>
                      <path d="M8 2v1a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V2"></path>
                      <path d="M6 4h12l-1 7H7z"></path>
                    </svg>
                    <span>PIN</span>
                  </button>
                )}
              </div>
            </div>
            
            <div className="top-bar__filter-item">
              <select 
                className={`top-bar__filter-select ${isManagerDropdownDisabled ? 'disabled' : ''}`}
                value={selectedManager} 
                onChange={handleManagerChange}
                disabled={isManagerDropdownDisabled}
                title={
                  isManagerDropdownDisabled 
                    ? 'Manager selection is only available when "All Years" is selected'
                    : ''
                }
              >
                <option value="">Select Manager...</option>
                {isAdmin && <option value="All">All Managers</option>}
                {managerList.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="top-bar__filter-item" style={{ display: 'none' }}>
              <label className="top-bar__filter-label">Min Groups: {minTotal}</label>
              <input 
                className="top-bar__range-input"
                type="range" 
                min="0" 
                max={sliderMaxValues?.maxGroups || 20} 
                value={minTotal} 
                onChange={e => setMinTotal(+e.target.value)} 
              />
            </div>
            
            <div className="top-bar__filter-item" style={{ display: 'none' }}>
              <label className="top-bar__filter-label">Min Largest: {minLargest}</label>
              <input 
                className="top-bar__range-input"
                type="range" 
                min="0" 
                max={sliderMaxValues?.maxLargest || 100} 
                value={minLargest} 
                onChange={e => setMinLargest(+e.target.value)} 
              />
            </div>

            <div className="top-bar__action-group">
              <div className="top-bar__filter-item top-bar__filter-item--with-left-border">
                <button
                  className={`top-bar__settings-btn ${!canManage ? 'disabled' : ''}`}
                  onClick={canManage ? () => setShowFileManagement(true) : undefined}
                  title={!canManage ? 'File Management is only available to admin users' : 'File Management & Settings'}
                  disabled={!canManage}
                  style={{
                    color: !canManage ? 'var(--color-text-disabled, #ccc)' : 'var(--color-text-secondary)',
                    cursor: !canManage ? 'not-allowed' : 'pointer',
                    opacity: !canManage ? 0.5 : 1
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={!canManage ? 'var(--color-text-disabled, #ccc)' : 'var(--color-text-secondary)'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06-.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 -1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  <span style={{ color: !canManage ? 'var(--color-text-disabled, #ccc)' : 'var(--color-text-secondary)' }}>Manage</span>
                </button>
              </div>
              <div className="top-bar__filter-item top-bar__filter-item--with-right-border">
                <button
                  className="top-bar__settings-btn"
                  onClick={() => {
                    // Reset all filters and state back to initial page load state
                    if (typeof resetFilters === 'function') resetFilters();
                    if (typeof setSelectedYear === 'function') setSelectedYear('All');
                    if (typeof unpinAllClients === 'function') unpinAllClients();
                    
                    // Reset pinned list state (search, filters, sorting, locks)
                    if (typeof setPinnedSearchQuery === 'function') setPinnedSearchQuery('');
                    if (typeof setActiveTierFilters === 'function') setActiveTierFilters(new Set());
                    if (typeof setSortConfig === 'function') setSortConfig({ key: null, direction: 'ascending' });
                    if (typeof setLockedClients === 'function') setLockedClients(new Set());
                    
                    // Reset app-level state
                    if (typeof setUseTierColors === 'function') setUseTierColors(false);
                    if (typeof setIsLoadingClients === 'function') setIsLoadingClients(false);
                    
                    // Reset view preferences to defaults
                    if (typeof toggleViewType === 'function' && pinnedViewType !== 'table') toggleViewType();
                    if (typeof toggleUniformColor === 'function' && useUniformColor) toggleUniformColor();
                    if (typeof toggleHardPin === 'function' && isHardPinned) toggleHardPin();
                    
                    // Reset country sidebar (hide it if shown)
                    if (typeof setSelectedCountry === 'function') setSelectedCountry(null);
                  }}
                  title="Reset all filters, clear pinned clients, unlock all locked items, and restore page to initial load state"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-secondary)' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2v6h-6"></path>
                    <path d="M3 12a9 9 0 0 1 15-7.36L21 8"></path>
                    <path d="M21 12a9 9 0 1 1-9-9"></path>
                  </svg>
                  <span style={{ color: 'var(--color-text-secondary)' }}>Reset Data</span>
                </button>
              </div>
            </div>

            <div className="top-bar__filter-item top-bar__user-menu-container" ref={userMenuRef}>
              <button 
                className="top-bar__user-menu-btn"
                onClick={handleUserMenuToggle}
                title={`User Menu - ${user?.username || user}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </button>
              
              {showUserMenu && (
                <div className="top-bar__user-dropdown">
                  <div className="top-bar__user-info">
                    <span className="top-bar__user-label">Logged in as:</span>
                    <span className="top-bar__username">{user?.username || user}</span>
                  </div>
                  <div className="top-bar__dropdown-divider"></div>
                  <button className="top-bar__logout-btn" onClick={handleLogout}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                      <polyline points="16 17 21 12 16 7"></polyline>
                      <line x1="21" y1="12" x2="9" y2="12"></line>
                    </svg>
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
      </div>

      {/* File Management Modal */}
      {showFileManagement && (
        <FileManagementModal
          isOpen={showFileManagement}
          onClose={() => setShowFileManagement(false)}
          managerList={managerList}      
          pinnedClients={pinnedClients}
          clientData={clientData}  
        />
      )}
    </>
  );
};

export default TopBar;
